import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";

import Loaders from "components/Common/Loaders";
import TeamPerformanceCustomTable from "components/Common/TeamPerformanceCustomTable";
import {
    AsyncClientSelect,
    AsyncUserSelect,
    StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { fetchLeadsAddedTeamOptions } from "model/LeadsAddedReportModel";
import { submitBackendPerformanceReportFilterData } from "model/BackendPerformanceReportModel";

const BASE_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
    { Header: "Review Status", accessor: "reviewStatus", isReviewStatusHtml: true },
];

const TRAILING_TABLE_COLUMNS = [
    { Header: "Last Day when email was sent", accessor: "lastDayWhenEmailWasSent" },
];

const normalizeStatusKey = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const buildStatusAccessor = (index) => `status_${index}`;

const findFirstClientWithStatusColumns = (teams = []) => {
    for (const team of Array.isArray(teams) ? teams : []) {
        for (const member of Array.isArray(team?.members) ? team.members : []) {
            for (const client of Array.isArray(member?.clients) ? member.clients : []) {
                if (Array.isArray(client?.statusColumns) && client.statusColumns.length) {
                    return client;
                }
            }
        }
    }
    return null;
};

const extractStatusColumnDefinitions = (teams = []) => {
    const firstClient = findFirstClientWithStatusColumns(teams);
    if (!firstClient) return [];

    return firstClient.statusColumns
        .map((statusColumn = {}) => {
            const statusKey = String(statusColumn?.statusKey ?? "").trim();
            return {
                statusKey,
                statusLabel: String(statusColumn?.statusLabel ?? statusKey).trim(),
                isClickable: statusColumn?.isClickable === true,
            };
        })
        .filter((definition) => definition.statusKey);
};

const buildTableColumns = (statusColumnDefinitions = []) => {
    const statusColumns = (Array.isArray(statusColumnDefinitions)
        ? statusColumnDefinitions
        : []
    ).map((definition, index) => ({
        Header: definition.statusLabel,
        accessor: buildStatusAccessor(index),
        statusKey: definition.statusKey,
        isStatusValue: true,
        isClickable: definition.isClickable === true,
    }));

    return [...BASE_TABLE_COLUMNS, ...statusColumns, ...TRAILING_TABLE_COLUMNS];
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const backendPerformanceReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const formatDateValue = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const parseReviewStatusLabel = (reviewStatusHtml = "") => {
    const raw = String(reviewStatusHtml ?? "").trim();
    if (!raw) return "";
    return raw.replace(/<[^>]*>/g, "").trim();
};

const resolveStatusDisplayValue = (statusColumn = {}) => {
    if (statusColumn?.hasValue === false) return "";
    const displayValue = statusColumn?.displayValue;
    if (displayValue === null || displayValue === undefined) return "";
    return displayValue;
};

const mapClientRow = (client = {}, serialNumber = 1, statusColumnDefinitions = []) => {
    const definitions = Array.isArray(statusColumnDefinitions) ? statusColumnDefinitions : [];
    const statusKeyToIndex = new Map(
        definitions.map((definition, index) => [
            normalizeStatusKey(definition.statusKey),
            index,
        ])
    );

    const mappedRow = {
        ...client,
        sn: client?.sn ?? client?.serialNumber ?? client?.sN ?? serialNumber,
        client: client?.client ?? client?.clientName ?? "",
        clientId: client?.clientId ?? "",
        reviewStatusHtml: String(client?.reviewStatusHtml ?? "").trim(),
        reviewStatus:
            client?.reviewStatus ??
            client?.review_status ??
            parseReviewStatusLabel(client?.reviewStatusHtml),
        lastDayWhenEmailWasSent:
            client?.lastEmailSentRelative ??
            client?.lastDayWhenEmailWasSent ??
            client?.last_day_when_email_sent ??
            formatDateValue(client?.lastEmailSentIso ?? client?.lastEmailSentDate),
    };

    definitions.forEach((_, labelIndex) => {
        mappedRow[buildStatusAccessor(labelIndex)] = "";
    });

    (Array.isArray(client?.statusColumns) ? client.statusColumns : []).forEach(
        (statusColumn = {}) => {
            const labelIndex = statusKeyToIndex.get(
                normalizeStatusKey(statusColumn?.statusKey)
            );
            if (labelIndex === undefined) return;
            mappedRow[buildStatusAccessor(labelIndex)] =
                resolveStatusDisplayValue(statusColumn);
        }
    );

    return mappedRow;
};

const mapNestedTeams = (teams = [], statusColumnDefinitions = []) =>
    (Array.isArray(teams) ? teams : []).map((team = {}) => ({
        team: team?.team ?? team?.teamName ?? "",
        teamLead: team?.teamLead ?? team?.teamLeadName ?? team?.team_lead ?? "",
        members: (Array.isArray(team?.members) ? team.members : []).map((member = {}) => ({
            name: member?.name ?? member?.userName ?? member?.memberName ?? "",
            rows: (Array.isArray(member?.clients) ? member.clients : []).map(
                (client, index) => mapClientRow(client, index + 1, statusColumnDefinitions)
            ),
        })),
    }));

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const teams = Array.isArray(nestedData?.teams)
        ? nestedData.teams
        : Array.isArray(responseData?.teams)
            ? responseData.teams
            : [];

    return { teams };
};

const BackendPerformanceReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        teamId: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
    });
    const [teamOptions, setTeamOptions] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [statusColumnDefinitions, setStatusColumnDefinitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => reportData, [reportData]);
    const tableColumns = useMemo(
        () => buildTableColumns(statusColumnDefinitions),
        [statusColumnDefinitions]
    );

    useEffect(() => {
        const loadTeamOptions = async () => {
            const teams = await fetchLeadsAddedTeamOptions();
            setTeamOptions(Array.isArray(teams) ? teams : []);
        };
        loadTeamOptions();
    }, []);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        teamId: strOrNull(filterForm?.teamId),
        userId: strOrNull(filterForm?.userId),
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getBackendPerformanceReportList = async () => {
        try {
            await backendPerformanceReportFilterSchema.validate(filterForm, {
                abortEarly: false,
            });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitBackendPerformanceReportFilterData(buildFilterPayload());
            const { teams } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setStatusColumnDefinitions([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const nextStatusColumnDefinitions = extractStatusColumnDefinitions(teams);
            setStatusColumnDefinitions(nextStatusColumnDefinitions);
            setReportData(mapNestedTeams(teams, nextStatusColumnDefinitions));
        } catch (error) {
            toast.error(error?.message || "Unable to load backend performance report");
            setReportData([]);
            setStatusColumnDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleTeamChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            teamId: option?.value || "",
        }));
    };

    return (
        <React.Fragment>
            <div className="page-content backend-performance-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Backend Performance Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`backend-performance-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportFiltered"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">Team</span>
                                        <StaticFilterSelect
                                            options={teamOptions}
                                            placeholder="Select Team"
                                            value={filterForm?.teamId || ""}
                                            onChange={handleTeamChange}
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">User</span>
                                        <AsyncUserSelect
                                            key={`backend-performance-user-${filterForm?.userId || ""}`}
                                            pageName="BackendPerformanceReport"
                                            value={filterForm?.userId || ""}
                                            initialOption={buildUserAsyncFilterInitialOption(
                                                filterForm?.userId,
                                                filterForm[getUserAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyUserAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select User"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" className="text-md-end" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getBackendPerformanceReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                                {/* <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end justify-content-end mt-1">
                                    <Col xs="12" md={{ size: 2, offset: 10 }} className="text-md-end">
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getBackendPerformanceReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row> */}
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <TeamPerformanceCustomTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    isActionRequired={false}
                                    pageName="BackendPerformanceReport"
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

BackendPerformanceReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(BackendPerformanceReport);
